// frontend/src/pages/BoardPage.jsx — 사내게시판(선개발, 사이드바 미노출).
// /board 직접 접근으로 미리보기. 노출 결정 시 AppSidebar NAV에 추가.
import { useState, useEffect, useCallback, useRef } from 'react';
import AppShell from '../components/AppShell';
import { boardAPI } from '../api/client';
import { IconArrowLeft, IconMessageCircle, IconPinned, IconPlus, IconSpeakerphone } from '@tabler/icons-react';
import { card as cardBase, input as inputBase } from '../utils/uiStyles';
import { Page, PageHeader, Button, PillTabs, SearchInput, Tag, Avatar, EmptyState, LoadingState, ErrorState } from '../components/ui';
import { alertDialog, confirmDialog } from '../components/AppDialog';

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s.endsWith('Z') ? s : s + 'Z');
  return d.toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const CATS = [
  { key: 'general', label: '일반' },
  { key: 'info', label: '정보공유' },
  { key: 'free', label: '자유' },
  { key: 'suggestion', label: '건의' },
];
const catLabel = (k) => (CATS.find((c) => c.key === k) || {}).label || k;

export default function BoardPage() {
  const me = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [filter, setFilter] = useState('');     // '' = 전체
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null);   // 상세(전체 객체)
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category: 'general', pinned: false });
  const [editingId, setEditingId] = useState(null);
  const [comment, setComment] = useState('');
  const isAdmin = me.role === 'admin';
  const postParamRef = useRef('');

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr('');
    boardAPI.list({ category: filter || undefined, q: search || undefined })
      .then((r) => setPosts(r.data.posts || []))
      .catch(() => { setPosts([]); setLoadErr('게시글을 불러오지 못했습니다.'); })
      .finally(() => setLoading(false));
  }, [filter, search]);
  useEffect(() => { load(); }, [load]);

  const openPost = useCallback((id) => boardAPI.get(id).then((r) => setActive(r.data)).catch(() => {}), []);

  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get('post');
    if (!raw || postParamRef.current === raw) return;
    postParamRef.current = raw;
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.hash || ''}`);
    const postId = Number(raw);
    if (!Number.isInteger(postId) || postId <= 0) return;
    openPost(postId);
  }, [openPost]);

  const submit = async () => {
    if (!form.title.trim()) { alertDialog('제목을 입력하세요.'); return; }
    try {
      if (editingId) await boardAPI.update(editingId, form);
      else await boardAPI.create(form);
      setShowForm(false); setEditingId(null); setForm({ title: '', content: '', category: 'general', pinned: false });
      load();
      if (editingId && active && active.id === editingId) openPost(editingId);
    } catch (e) { alertDialog('저장 실패: ' + (e.response?.data?.detail || e.message)); }
  };

  const startEdit = (p) => { setForm({ title: p.title, content: p.content, category: p.category, pinned: p.pinned }); setEditingId(p.id); setShowForm(true); setActive(null); };
  const remove = async (p) => {
    if (!(await confirmDialog('이 글을 삭제할까요?', { danger: true }))) return;
    try { await boardAPI.remove(p.id); setActive(null); load(); } catch (e) { alertDialog('삭제 실패: ' + (e.response?.data?.detail || e.message)); }
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    try { await boardAPI.addComment(active.id, comment.trim()); setComment(''); openPost(active.id); load(); }
    catch (e) { alertDialog('댓글 실패: ' + (e.response?.data?.detail || e.message)); }
  };
  const delComment = async (cid) => {
    if (!(await confirmDialog('댓글을 삭제할까요?', { danger: true }))) return;
    try { await boardAPI.removeComment(cid); openPost(active.id); } catch (e) { alertDialog('삭제 실패: ' + (e.response?.data?.detail || e.message)); }
  };
  const canEdit = (o) => o && (o.user_id === me.id || isAdmin);

  const Inp = { ...inputBase, width: '100%', boxSizing: 'border-box', background: 'var(--gw-surface, #fff)', color: 'var(--gw-text, #32312e)' };
  const catKey = filter || 'all';

  return (
    <AppShell app="board" style={{ fontFamily: 'var(--gw-font)', background: 'var(--gw-bg, #fff)' }}>
      <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
        <Page>
          <PageHeader
            icon={IconSpeakerphone}
            tone="blue"
            title="사내게시판"
            subtitle="직원 누구나 글을 올리고 댓글로 소통하는 공간입니다."
            actions={(
              <>
                <span style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', alignSelf: 'center' }}>게시글 {posts.length}건</span>
                {!active && (
                  <Button variant="primary" icon={IconPlus} onClick={() => { setEditingId(null); setForm({ title: '', content: '', category: 'general', pinned: false }); setShowForm((v) => !v); }}>글쓰기</Button>
                )}
              </>
            )}
          />

          {!active && (
            <>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                  <PillTabs ariaLabel="게시판 분류" idBase="board-cat" value={catKey} onChange={(k) => setFilter(k === 'all' ? '' : k)} items={[{ key: 'all', label: '전체' }, ...CATS]} />
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="검색" width="min(240px, 100%)" />
                </div>
              </div>

              {showForm && (
                <div style={{ ...cardBase, marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} style={{ ...Inp, width: '140px' }}>
                      {CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                    <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="제목" style={{ ...Inp, flex: 1, minWidth: '220px' }} />
                  </div>
                  <textarea value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} placeholder="내용" rows={6} style={{ ...Inp, resize: 'vertical' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    {isAdmin && <label style={{ fontSize: '13px', color: 'var(--gw-text, #32312e)', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.pinned} onChange={(e) => setForm((f) => ({ ...f, pinned: e.target.checked }))} /> 상단 고정</label>}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <Button variant="secondary" onClick={() => { setShowForm(false); setEditingId(null); }}>취소</Button>
                      <Button variant="primary" onClick={submit}>{editingId ? '수정' : '등록'}</Button>
                    </div>
                  </div>
                </div>
              )}

              <div role="tabpanel" id={`board-cat-panel-${catKey}`} aria-labelledby={`board-cat-tab-${catKey}`} style={{ ...cardBase, padding: 0, overflow: 'hidden' }}>
                {loading && <LoadingState>불러오는 중…</LoadingState>}
                {!loading && loadErr && <div style={{ padding: '14px' }}><ErrorState message={loadErr} /></div>}
                {!loading && !loadErr && posts.length === 0 && <EmptyState description="게시글이 없습니다." />}
                {!loading && !loadErr && posts.map((p, i) => (
                  <div key={p.id} onClick={() => openPost(p.id)}
                    style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: '10px', rowGap: '4px', padding: '12px 16px', borderTop: i === 0 ? 'none' : '1px solid var(--gw-line, #eeeeec)', cursor: 'pointer', minWidth: 0 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gw-hover, #efefed)'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    {p.pinned && <span title="고정" style={{ color: 'var(--gw-secondary, #64635f)', display: 'inline-flex', flexShrink: 0 }}><IconPinned size={15} stroke={1.8} /></span>}
                    <span style={{ flexShrink: 0 }}><Tag tone="blue">{catLabel(p.category)}</Tag></span>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gw-text, #32312e)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 160px' }}>{p.title}</span>
                    <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '10px', flexShrink: 0, fontSize: '12px', color: 'var(--gw-muted, #92918d)' }}>
                      {p.comment_count > 0 && <span style={{ fontSize: '12px', color: 'var(--gw-blue, #2383e2)', fontWeight: 600, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '3px' }}><IconMessageCircle size={14} stroke={1.8} />{p.comment_count}</span>}
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', maxWidth: '120px', minWidth: 0 }}>
                        {p.author_name && <Avatar name={p.author_name} size={19} />}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--gw-secondary, #64635f)' }}>{p.author_name || '-'}</span>
                      </span>
                      <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(p.created_at)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {active && (
            <div style={{ ...cardBase, padding: '22px' }}>
              <Button variant="quiet" size="sm" icon={IconArrowLeft} onClick={() => setActive(null)} style={{ marginBottom: '14px', marginLeft: '-8px' }}>목록</Button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                {active.pinned && <span style={{ color: 'var(--gw-secondary, #64635f)', display: 'inline-flex' }}><IconPinned size={16} stroke={1.8} /></span>}
                <Tag tone="blue">{catLabel(active.category)}</Tag>
                <h2 style={{ fontSize: '19px', fontWeight: 620, color: 'var(--gw-text, #32312e)', margin: 0, wordBreak: 'break-word' }}>{active.title}</h2>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', marginBottom: '16px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span>{active.author_name || '-'}</span><span>·</span><span>{fmtDate(active.created_at)}</span><span>·</span><span>조회 {active.views}</span>
                {canEdit(active) && <span style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                  <Button variant="secondary" size="sm" onClick={() => startEdit(active)}>수정</Button>
                  <Button variant="danger" size="sm" onClick={() => remove(active)}>삭제</Button>
                </span>}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--gw-text, #32312e)', lineHeight: 1.7, whiteSpace: 'pre-wrap', minHeight: '80px', paddingBottom: '18px', borderBottom: '1px solid var(--gw-line, #eeeeec)' }}>{active.content || ' '}</div>

              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)', marginBottom: '10px' }}>댓글 {(active.comments || []).length}</div>
                {(active.comments || []).map((c) => (
                  <div key={c.id} style={{ display: 'flex', gap: '10px', padding: '10px 0', borderTop: '1px solid var(--gw-line, #eeeeec)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--gw-muted, #92918d)', marginBottom: '2px' }}>{c.author_name && <Avatar name={c.author_name} size={19} />}{c.author_name || '-'} · {fmtDate(c.created_at)}</div>
                      <div style={{ fontSize: '14px', color: 'var(--gw-text, #32312e)', whiteSpace: 'pre-wrap' }}>{c.content}</div>
                    </div>
                    {(c.user_id === me.id || isAdmin) && <Button variant="quiet" size="sm" onClick={() => delComment(c.id)}>삭제</Button>}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                  <input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addComment(); }} placeholder="댓글 입력" style={{ ...Inp, flex: '1 1 260px', minWidth: '180px' }} />
                  <Button variant="primary" onClick={addComment}>등록</Button>
                </div>
              </div>
            </div>
          )}
        </Page>
      </div>
    </AppShell>
  );
}
